import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import useDailyReminder from "@/hooks/useDailyReminder";
import ThemeToggle from "@/components/ThemeToggle";
import { Home, BookOpen, MessageCircleHeart, NotebookPen, User, LogOut, Feather, BookMarked } from "lucide-react";

const NAV = [
  { to: "/", label: "Home", icon: Home, id: "home" },
  { to: "/scripture", label: "Scripture", icon: BookMarked, id: "scripture" },
  { to: "/practices", label: "Practices", icon: BookOpen, id: "practices" },
  { to: "/guide", label: "Guide", icon: MessageCircleHeart, id: "guide" },
  { to: "/journal", label: "Journal", icon: NotebookPen, id: "journal" },
  { to: "/profile", label: "Profile", icon: User, id: "profile" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { backdrop, theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  useDailyReminder();
  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {backdrop?.src && (
        <div className="fixed inset-0 pointer-events-none" style={{ zIndex: -1 }} aria-hidden="true" data-testid="app-backdrop">
          <img src={backdrop.src} alt="" className="w-full h-full object-cover" style={{ opacity: theme.dark ? 0.06 : 0.09 }} />
        </div>
      )}
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex md:w-64 lg:w-72 shrink-0 flex-col justify-between border-r border-border bg-card/60 px-7 py-10 sticky top-0 h-screen">
        <div>
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3 mb-14">
            <span className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              <Feather className="w-4 h-4" />
            </span>
            <span className="font-heading text-3xl font-semibold tracking-tight leading-none">Attune</span>
          </Link>
          <nav className="flex flex-col gap-1">
            {NAV.map(({ to, label, icon: Icon, id }) => (
              <Link key={to} to={to} data-testid={`nav-${id}-link`}
                className={`group flex items-center gap-3 px-4 py-3 rounded-sm transition-colors duration-300 ${
                  isActive(to) ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}>
                <Icon className="w-[18px] h-[18px]" />
                <span className="text-sm tracking-wide">{label}</span>
              </Link>
            ))}
          </nav>
        </div>
        <div className="border-t border-border pt-6">
          <p className="font-heading text-xl leading-tight">{user?.name}</p>
          <p className="text-xs text-muted-foreground mb-4 truncate">{user?.email}</p>
          <div className="flex items-center justify-between">
            <button data-testid="logout-button" onClick={() => { logout(); navigate("/auth"); }}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive transition-colors">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
            <ThemeToggle />
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden flex items-center justify-between px-5 py-4 border-b border-border bg-card/80 sticky top-0 z-20 backdrop-blur">
        <Link to="/" className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
            <Feather className="w-4 h-4" />
          </span>
          <span className="font-heading text-2xl font-semibold">Attune</span>
        </Link>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <button data-testid="logout-button-mobile" onClick={() => { logout(); navigate("/auth"); }} className="text-muted-foreground">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 min-w-0 pb-24 md:pb-0">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-20 bg-card/95 backdrop-blur border-t border-border flex justify-around py-2">
        {NAV.map(({ to, label, icon: Icon, id }) => (
          <Link key={to} to={to} data-testid={`mobile-nav-${id}-link`}
            className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-sm ${isActive(to) ? "text-primary" : "text-muted-foreground"}`}>
            <Icon className="w-5 h-5" />
            <span className="text-[10px] tracking-wide">{label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
}
